import React, { useEffect, useMemo, useRef } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { Flag } from "@phosphor-icons/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DistributionDatum {
  /** Category label shown on the y-axis, e.g. "Partial Physical" */
  label: string;
  /** Count for the category, e.g. number of trials at this prompt level */
  value: number;
}

export interface TargetDistributionChartProps {
  /** Descriptive caption above the chart, e.g. "Breakdown of prompt levels used across trials" */
  title: string;
  /** One bar per category, top to bottom */
  data: DistributionDatum[];
  /** X-axis label — defaults to "Prompt Level" per the Figma spec */
  xAxisLabel?: string;
  /** Callback when "Add Marker" is clicked — button hidden when omitted */
  onAddMarker?: () => void;
  /** Fires with the underlying Chart.js instance on mount, and with null on unmount. */
  onChartReady?: (chart: ChartJS<"bar"> | null) => void;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

const fontFamily = "BasierCircle, sans-serif";

// ---------------------------------------------------------------------------
// TargetDistributionChart
// ---------------------------------------------------------------------------

/**
 * Horizontal bar chart for target detail views — shows how trials distribute
 * across categories (e.g. prompt levels used across trials). One orange bar
 * per category with whole-number counts along the x-axis.
 *
 * Figma: "Target Chart" type=Distribution (node 5228:6520).
 *
 * @param props
 * @param props.title - Descriptive caption above the chart
 * @param props.data - One {label, value} entry per category
 * @param [props.xAxisLabel="Prompt Level"] - X-axis label
 * @param [props.onAddMarker] - Add marker callback (hides button when omitted)
 * @param [props.onChartReady] - Fires with the Chart.js instance on mount, null on unmount
 * @param [props.sx] - Additional MUI sx overrides
 */
const TargetDistributionChart: React.FC<TargetDistributionChartProps> = ({
  title,
  data,
  xAxisLabel = "Prompt Level",
  onAddMarker,
  onChartReady,
  sx,
}) => {
  const theme = useTheme();
  const chartRef = useRef<ChartJS<"bar"> | null>(null);

  const setChartRef = (chart: ChartJS<"bar"> | null | undefined) => {
    const normalized = chart ?? null;
    chartRef.current = normalized;
    onChartReady?.(normalized);
  };

  // Cleanup the Chart instance on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  const p = theme.palette as any;
  const barColor: string = p.orange?.[400] ?? "#FFA726";
  const axisColor: string = p.grayscale?.[600] ?? "#798397";

  const chartData: ChartData<"bar"> = useMemo(
    () => ({
      labels: data.map((d) => d.label),
      datasets: [
        {
          label: title,
          data: data.map((d) => d.value),
          backgroundColor: barColor,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 20,
        },
      ],
    }),
    [data, title, barColor]
  );

  const chartOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      // Disabled intentionally — clinical data charts don't benefit from
      // intro animations and the instant render avoids a flash on re-mount.
      animation: false,
      layout: { padding: { top: 8, right: 8, bottom: 0, left: 0 } },
      scales: {
        x: {
          display: true,
          beginAtZero: true,
          grid: { color: theme.palette.divider },
          border: { display: false },
          ticks: {
            stepSize: 1,
            precision: 0,
            color: axisColor,
            font: { size: 12, family: fontFamily },
            padding: 4,
          },
          title: {
            display: true,
            text: xAxisLabel,
            color: axisColor,
            font: { size: 12, family: fontFamily },
          },
        },
        y: {
          display: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: axisColor,
            font: { size: 12, family: fontFamily },
            padding: 8,
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: theme.palette.background?.paper ?? "#fff",
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          titleFont: { size: 12, family: fontFamily },
          bodyFont: { size: 12, family: fontFamily },
        },
      },
    }),
    [axisColor, xAxisLabel, theme]
  );

  return (
    <Box
      component="section"
      aria-label="Target distribution chart"
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 4,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 6,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {title}
        </Typography>

        {onAddMarker && (
          <Button
            variant="text"
            size="small"
            color="primary"
            startIcon={<Flag size={16} />}
            onClick={onAddMarker}
          >
            Add Marker
          </Button>
        )}
      </Stack>

      {/* Chart area */}
      <Box aria-label="Chart area" sx={{ position: "relative", width: "100%", height: 300 }}>
        <Bar ref={setChartRef} data={chartData} options={chartOptions} />
      </Box>
    </Box>
  );
};

export default TargetDistributionChart;
